import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { MessageSquare, Heart, User, Send, DoorOpen, HandHelping, Phone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* Talk — /cef/talk/:serverId/:ref — the Living Citizens dialogue surface.
   Transparent overlay: the 3D citizen stands in front of you in the world; this is
   just the conversation chrome. Options by default; free text unlocks at level 2
   ("familiar"). Everything the NPC says is generated hub-side with full memory —
   every other player meets the same person. */

const LEVEL_COLORS = ["text-zinc-500", "text-sky-300", "text-teal-300", "text-emerald-300", "text-amber-300"];

function TypingText({ text, onDone }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    setShown(0);
    const t = setInterval(() => setShown((s) => {
      if (s >= text.length) { clearInterval(t); onDone && onDone(); return s; }
      return s + 2;
    }), 18);
    return () => clearInterval(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  return <span>{text.slice(0, shown)}{shown < text.length && <span className="inline-block w-1.5 h-3 bg-nc-blue/80 align-middle ml-0.5 animate-pulse" />}</span>;
}

export default function TalkCefPage() {
  const { serverId, ref } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  const [card, setCard] = useState(null);
  const [log, setLog] = useState([]);          // [{who: 'npc'|'me', text}]
  const [options, setOptions] = useState([]);
  const [freeText, setFreeText] = useState("");
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  const [ended, setEnded] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [log, typing]);

  const meet = useCallback(async () => {
    if (demo) {
      setCard({ name: "Emeka Abernathy", occupation: "teacher", archetype: "proud", level: 0,
                level_name: "stranger", affinity: 0, free_text: false, introduced: true, met_count: 3 });
      setLog([{ who: "npc", text: "Emeka Abernathy — teacher around North Market. \"Back when Bornwood kept its standards, this wouldn't fly.\"" }]);
      setOptions([{ id: "a", text: "How's your day been?" }, { id: "b", text: "Heard anything interesting?" }, { id: "c", text: "Take care." }]);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/life/meet`, { ref, token });
      setCard(r.data.card);
      setLog([{ who: "npc", text: r.data.greeting }]);
      if (r.data.first_meeting) {
        setOptions([{ id: "a", text: "Nice to meet you. How's your day been?" },
                    { id: "b", text: "What do you do around here?" },
                    { id: "c", text: "I'll let you get to it. Take care." }]);
      } else {
        setOptions([{ id: "a", text: "Good to see you too. What's new?" },
                    { id: "b", text: "Been thinking about our last chat." },
                    { id: "c", text: "Just passing through — take care." }]);
      }
      setTyping(true);
    } catch (e) { toast.error(e.response?.data?.detail || "They walk on by."); }
    setBusy(false);
  }, [serverId, ref, token, demo]);

  useEffect(() => { meet(); }, [meet]);

  const say = async (line) => {
    if (!line.trim() || busy) return;
    if (demo) {
      setLog((l) => [...l, { who: "me", text: line }, { who: "npc", text: "Ah, you know how it is. The city keeps its own hours — you just try to keep up." }]);
      return;
    }
    setBusy(true);
    setLog((l) => [...l, { who: "me", text: line }]);
    setOptions([]);
    setFreeText("");
    try {
      const r = await api.post(`/nc/servers/${serverId}/life/talk`, { ref, token, line });
      setTyping(true);
      setLog((l) => [...l, { who: "npc", text: r.data.reply }]);
      setOptions(r.data.end ? [] : r.data.options);
      setEnded(r.data.end);
      setCard((c) => ({ ...c, affinity: r.data.affinity, level: r.data.level,
                         level_name: r.data.level_name, free_text: r.data.free_text }));
      if (r.data.free_text && !card?.free_text) toast.success("They trust you now — free chat unlocked");
    } catch (e) {
      toast.error(e.response?.data?.detail || "The conversation stalls.");
      setLog((l) => l.slice(0, -1));
    }
    setBusy(false);
  };

  const swapNumbers = async () => {
    if (busy) return;
    if (demo) {
      setCard((c) => ({ ...c, phone_shared: true }));
      toast.success("Preview: number saved — they can text you now");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/life/share-number`, { ref, token });
      setCard((c) => ({ ...c, phone_shared: true }));
      setLog((l) => [...l, { who: "npc", text: "They tap your number into their phone. A text lands a second later." }]);
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "They're not ready to swap numbers."); }
    setBusy(false);
  };


  return (
    <div data-testid="talk-page" className="fixed inset-0 bg-transparent flex flex-col items-center justify-end pb-[6vh] pointer-events-none">
      {demo && <PreviewRibbon />}
      <div className="w-full max-w-2xl px-4 flex flex-col gap-3 pointer-events-auto">
        {/* citizen chip */}
        {card && (
          <div data-testid="talk-card" className="self-start flex items-center gap-3 bg-black/75 backdrop-blur-md border border-white/15 px-4 py-2.5"
               style={{ animation: "ncFadeUp 0.4s ease both" }}>
            <div className="w-9 h-9 rounded-full bg-nc-blue/20 border border-nc-blue/50 flex items-center justify-center">
              <User className="w-4.5 h-4.5 text-nc-blue" />
            </div>
            <div>
              <p className="font-sans font-semibold text-sm text-zinc-100">{card.name}</p>
              <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500">
                {card.occupation ? `${card.occupation} · ` : ""}{card.archetype}
              </p>
            </div>
            <div className="ml-4 flex items-center gap-1.5" title={`${card.affinity} affinity`}>
              <Heart className={`w-3.5 h-3.5 ${LEVEL_COLORS[card.level] || LEVEL_COLORS[0]}`} />
              <span data-testid="talk-level" className={`font-mono text-[9px] uppercase tracking-[0.15em] ${LEVEL_COLORS[card.level] || LEVEL_COLORS[0]}`}>
                {card.level_name}
              </span>
            </div>
          </div>
        )}

        {/* conversation log */}
        <div ref={scrollRef} data-testid="talk-log"
             className="bg-black/70 backdrop-blur-md border border-white/10 p-4 max-h-[38vh] overflow-y-auto flex flex-col gap-2.5"
             style={{ animation: "ncFadeUp 0.5s ease both" }}>
          {log.map((m, i) => (
            <div key={i} className={m.who === "me" ? "self-end max-w-[80%]" : "self-start max-w-[85%]"}>
              <p className={m.who === "me"
                ? "font-mono text-xs text-zinc-300 bg-white/8 border border-white/10 px-3 py-2"
                : "font-mono text-xs text-zinc-100 bg-nc-blue/10 border border-nc-blue/25 px-3 py-2 leading-relaxed"}>
                {m.who === "npc" && i === log.length - 1 && typing
                  ? <TypingText text={m.text} onDone={() => setTyping(false)} />
                  : m.text}
              </p>
            </div>
          ))}
          {busy && <p className="font-mono text-[10px] text-zinc-500 animate-pulse self-start">{card?.name?.split(" ")[0] || "They"} is thinking…</p>}
        </div>

        {/* options / free text */}
        {!ended ? (
          <div data-testid="talk-options" className="flex flex-col gap-2" style={{ animation: "ncFadeUp 0.5s ease both" }}>
            {options.map((o) => (
              <button key={o.id} data-testid={`talk-option-${o.id}`} disabled={busy || typing}
                      onClick={() => say(o.text)}
                      className="self-start text-left font-mono text-xs text-zinc-200 bg-black/60 hover:bg-nc-blue/20 hover:border-nc-blue/60 border border-white/15 px-4 py-2.5 transition-colors duration-200 disabled:opacity-40">
                <MessageSquare className="w-3 h-3 inline mr-2 text-nc-blue" />{o.text}
              </button>
            ))}
            {card?.free_text && (
              <div className="flex gap-2 mt-1">
                <input data-testid="talk-free-input" value={freeText} onChange={(e) => setFreeText(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && say(freeText)}
                       placeholder="Say anything…" maxLength={300}
                       className="flex-1 bg-black/70 border border-white/15 focus:border-nc-blue px-3 py-2.5 font-mono text-xs text-zinc-100 focus:outline-none" />
                <button data-testid="talk-free-send" onClick={() => say(freeText)} disabled={busy || !freeText.trim()}
                        className="bg-nc-blue hover:bg-nc-blue/80 text-white px-4 disabled:opacity-40 transition-colors">
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            {card?.level >= 2 && !card?.phone_shared && (
              <button data-testid="talk-swap-numbers" disabled={busy}
                      onClick={swapNumbers}
                      className="self-start font-mono text-[10px] uppercase tracking-[0.15em] border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 px-4 py-2.5 transition-colors disabled:opacity-40 flex items-center gap-2">
                <Phone className="w-3.5 h-3.5" /> Swap numbers
              </button>
            )}
            {card?.phone_shared && (
              <p data-testid="talk-number-shared" className="self-start font-mono text-[9px] uppercase tracking-[0.18em] border border-emerald-400/30 text-emerald-300 bg-emerald-400/5 px-3 py-2">
                In your Messages — they can text you now
              </p>
            )}
            {card?.level >= 3 && !demo && (
              <button data-testid="talk-favor-ask" disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          const r = await api.post(`/nc/servers/${serverId}/life/favor/request`, { ref, token });
                          setLog((l) => [...l, { who: "npc", text: r.data.favor.brief }]);
                          toast.success(r.data.message + " Track it from their contact in Messages.");
                        } catch (e) { toast.error(e.response?.data?.detail || "They're fine, honestly."); }
                        setBusy(false);
                      }}
                      className="self-start font-mono text-[10px] uppercase tracking-[0.15em] border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 px-4 py-2.5 transition-colors disabled:opacity-40 flex items-center gap-2">
                <HandHelping className="w-3.5 h-3.5" /> They seem troubled — ask what's wrong
              </button>
            )}
          </div>
        ) : (
          <div data-testid="talk-ended" className="flex items-center gap-3">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">They head off. The city remembers this.</p>
            <button data-testid="talk-leave" onClick={() => window.close()}
                    className="font-mono text-[10px] uppercase tracking-[0.2em] border border-white/20 hover:border-white/50 text-zinc-300 px-4 py-2 transition-colors flex items-center gap-2">
              <DoorOpen className="w-3.5 h-3.5" /> Walk away
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
